import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { colors } from "../../../utils";
import { DISCOVER_PATH } from "../../DiscoverEntry/discoverEntryTarget";
import { HEADER_ICON_SIZE, IconWrapper } from "./styles";
import { NavEntryMenuItem } from "./NavEntryMenuItem";
import { isSectionActive } from "./headerNavActive";

/**
 * Explore (Discover) header nav entry — Discover redesign piece 4 (DL-098).
 *
 * Gordon's spec asks for "a persistent Discover in the top nav for everyone".
 * MEASURED 2026-08-01 on bundle main.f563cd08.js: there was no such entry
 * anywhere. `/explore` was reachable only by typing the URL, by the Command
 * Palette (which requires knowing the palette exists), or by luck — the same
 * "built but URL-only" shape FollowerFeedNavEntry was created to fix.
 *
 * NOT flag-gated, deliberately, unlike its Capture/Chat/Following neighbours:
 * those gate a feature that may not exist in a given environment. `/explore` is
 * live in every environment today (521 wheels measured on staging), so a flag
 * here would only be a way to keep the entry dark.
 *
 * NAMING (RESOLVED, Gordon 2026-08-06): "the page is called Discover, not
 * Explore." The label, tooltip and aria-label all read "Discover" now, matching
 * the destination page's own <h1> and the Command Palette entry. The route
 * itself (`/explore`) and every internal name are unchanged — this is a
 * user-facing copy change only, not a URL/route rename.
 *
 * Same Tooltip + IconWrapper + a11y contract as the neighbouring header icons
 * (line-art icon, role/tabIndex/aria-label, Enter/Space activation).
 */
const ExploreNavEntry = ({ variant = "icon", ...rest }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();

  const go = () => nav(DISCOVER_PATH);
  const active = isSectionActive(pathname, DISCOVER_PATH);

  if (variant === "menu") {
    return (
      <NavEntryMenuItem
        label="Discover"
        onActivate={go}
        icon={
          <ExploreOutlinedIcon
            aria-hidden="true"
            style={{ color: colors.PrimaryPurple, fontSize: 22 }}
          />
        }
        {...rest}
      />
    );
  }

  return (
    <Tooltip title="Discover">
      <IconWrapper
        className="explore-button"
        role="button"
        tabIndex={0}
        aria-label="Discover"
        aria-current={active ? "page" : undefined}
        isActive={active}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            go();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <ExploreOutlinedIcon
          aria-hidden="true"
          style={{ color: colors.PrimaryPurple, fontSize: HEADER_ICON_SIZE }}
        />
      </IconWrapper>
    </Tooltip>
  );
};

export default ExploreNavEntry;
